import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Publish as PublishIcon,
  Security as SecurityIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { sitesService } from '../services/sitesService';

interface Site {
  id: string;
  name: string;
  domain: string;
  status: 'staged' | 'live' | 'archived' | 'maintenance';
  layout: 'basic' | 'ecommerce' | 'portfolio' | 'blog';
  client_name: string;
  config_json: any;
  created_at: string;
  updated_at: string;
}

interface DomainConfig {
  domain: string;
  sslStatus: {
    isValid: boolean;
    expiry: string | null;
    error: string | null;
  };
  dnsStatus: {
    resolved: boolean;
    ip: string | null;
    error: string | null;
  };
  lastChecked: string;
}

interface FileStructure {
  sitePath: string;
  fileStructure: FileNode[];
  domain: string;
}

interface FileNode {
  name: string;
  type: 'directory' | 'file';
  path: string;
  children?: FileNode[];
  size?: number;
  modified?: string;
}

const SitesPage: React.FC = () => {
  useAuth(); // Ensure authenticated
  const theme = useTheme();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [domainConfig, setDomainConfig] = useState<DomainConfig | null>(null);
  const [fileStructure, setFileStructure] = useState<FileStructure | null>(null);
  const [showDomainDialog, setShowDomainDialog] = useState(false);
  const [showContentEditor, setShowContentEditor] = useState(false);
  const [showFileStructure, setShowFileStructure] = useState(false);
  const [editedContent, setEditedContent] = useState<any>(null);
  const [sslLoading, setSslLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      setLoading(true);
      const response = await sitesService.getSites();
      setSites(response.data.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load sites');
    } finally {
      setLoading(false);
    }
  };

  const loadDomainConfig = async (siteId: string) => {
    try {
      const response = await sitesService.getDomainConfig(siteId);
      setDomainConfig(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load domain config');
    }
  };

  const loadFileStructure = async (siteId: string) => {
    try {
      const response = await sitesService.getFileStructure(siteId);
      setFileStructure(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load file structure');
    }
  };

  const handleSSLRenewal = async (siteId: string) => {
    try {
      setSslLoading(true);
      await sitesService.updateSSL(siteId);
      await loadDomainConfig(siteId);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to renew SSL certificate');
    } finally {
      setSslLoading(false);
    }
  };

  const handlePublishSite = async (siteId: string) => {
    try {
      setPublishLoading(true);
      await sitesService.publishSite(siteId);
      await loadSites();
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to publish site');
    } finally {
      setPublishLoading(false);
    }
  };

  const handleSaveContent = async () => {
    if (!editingSite || !editedContent) return;

    try {
      await sitesService.updateContent(editingSite.id, {
        config_json: editedContent,
        autoPublish: false
      });
      await loadSites();
      setShowContentEditor(false);
      setEditingSite(null);
      setEditedContent(null);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save content');
    }
  };

  const renderFileTree = (nodes: FileNode[], level = 0) => {
    return nodes.map((node, index) => (
      <React.Fragment key={`${node.path}-${index}`}>
        <ListItem sx={{ pl: 2 + level * 2 }}>
          <ListItemIcon>
            {node.type === 'directory' ? <FolderIcon color="primary" /> : <FileIcon color="action" />}
          </ListItemIcon>
          <ListItemText 
            primary={node.name}
            secondary={node.type === 'file' && node.size ? `${(node.size / 1024).toFixed(1)} KB` : undefined}
            primaryTypographyProps={{ color: 'text.primary' }}
            secondaryTypographyProps={{ color: 'text.secondary' }}
          />
        </ListItem>
        {node.children && renderFileTree(node.children, level + 1)}
      </React.Fragment>
    ));
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 700, mb: 1 }}>
              Sites Management
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Manage website deployments and configurations
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* TODO: Add new site wizard */}}
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            Add Site
          </Button>
        </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            bgcolor: alpha(theme.palette.error.main, 0.9),
            color: 'common.white',
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {sites.map((site) => (
          <Grid item xs={12} md={6} lg={4} key={site.id}>
            <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6" component="h2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {site.name}
                  </Typography>
                  <Chip 
                    label={site.status} 
                    size="small"
                    sx={{
                      bgcolor: site.status === 'live' ? alpha(theme.palette.success.main, 0.1) :
                              site.status === 'staged' ? alpha(theme.palette.info.main, 0.1) :
                              alpha(theme.palette.text.disabled, 0.1),
                      color: site.status === 'live' ? theme.palette.success.main :
                            site.status === 'staged' ? theme.palette.info.main :
                            theme.palette.text.disabled,
                      border: `1px solid ${
                        site.status === 'live' ? alpha(theme.palette.success.main, 0.3) :
                        site.status === 'staged' ? alpha(theme.palette.info.main, 0.3) :
                        alpha(theme.palette.text.disabled, 0.3)
                      }`,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
                
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  {site.domain}
                </Typography>
                
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Client: {site.client_name}
                </Typography>
                
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Layout: {site.layout}
                </Typography>

                <Divider sx={{ my: 2, borderColor: 'divider' }} />

                <Box display="flex" gap={1} flexWrap="wrap">
                  <Tooltip title="Domain & SSL Info">
                    <IconButton 
                      size="small"
                      onClick={() => {
                        setEditingSite(site);
                        loadDomainConfig(site.id);
                        setShowDomainDialog(true);
                      }}
                      sx={{
                        color: 'primary.main',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                      }}
                    >
                      <SecurityIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="File Structure">
                    <IconButton 
                      size="small"
                      onClick={() => {
                        setEditingSite(site);
                        loadFileStructure(site.id);
                        setShowFileStructure(true);
                      }}
                      sx={{
                        color: 'info.main',
                        '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.1) },
                      }}
                    >
                      <StorageIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Edit Content">
                    <IconButton 
                      size="small"
                      onClick={() => {
                        setEditingSite(site);
                        setEditedContent(site.config_json);
                        setShowContentEditor(true);
                      }}
                      sx={{ color: 'text.secondary' }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="View Live Site">
                    <IconButton 
                      size="small"
                      onClick={() => window.open(`https://${site.domain}`, '_blank')}
                      sx={{ color: 'text.secondary' }}
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
              
              <CardActions>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={publishLoading ? <CircularProgress size={16} /> : <PublishIcon />}
                  onClick={() => handlePublishSite(site.id)}
                  disabled={publishLoading}
                  sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                >
                  Publish
                </Button>
                <Button size="small" onClick={() => loadSites()} sx={{ color: 'text.secondary' }}>
                  <RefreshIcon />
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Domain & SSL Dialog */}
      <Dialog 
        open={showDomainDialog} 
        onClose={() => setShowDomainDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle sx={{ color: 'text.primary' }}>
          Domain Configuration: {editingSite?.domain}
        </DialogTitle>
        <DialogContent>
          {domainConfig ? (
            <Box>
              <Accordion defaultExpanded sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}>
                  <Typography variant="h6">SSL Certificate Status</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" gutterBottom sx={{ color: 'text.secondary' }}>
                        <strong>Status:</strong> {domainConfig.sslStatus.isValid ? '✅ Valid' : '❌ Invalid'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" gutterBottom sx={{ color: 'text.secondary' }}>
                        <strong>Expiry:</strong> {domainConfig.sslStatus.expiry || 'Unknown'}
                      </Typography>
                    </Grid>
                    {domainConfig.sslStatus.error && (
                      <Grid item xs={12}>
                        <Alert severity="error">
                          {domainConfig.sslStatus.error}
                        </Alert>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        startIcon={sslLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                        onClick={() => handleSSLRenewal(editingSite!.id)}
                        disabled={sslLoading}
                        sx={{ color: 'primary.main', borderColor: 'primary.main' }}
                      >
                        Renew SSL Certificate
                      </Button>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}>
                  <Typography variant="h6">DNS Configuration</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" gutterBottom sx={{ color: 'text.secondary' }}>
                        <strong>Status:</strong> {domainConfig.dnsStatus.resolved ? '✅ Resolved' : '❌ Not Resolved'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" gutterBottom sx={{ color: 'text.secondary' }}>
                        <strong>IP Address:</strong> {domainConfig.dnsStatus.ip || 'Unknown'}
                      </Typography>
                    </Grid>
                    {domainConfig.dnsStatus.error && (
                      <Grid item xs={12}>
                        <Alert severity="error">
                          {domainConfig.dnsStatus.error}
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'text.secondary' }}>
                Last checked: {new Date(domainConfig.lastChecked).toLocaleString()}
              </Typography>
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={() => setShowDomainDialog(false)} sx={{ color: 'text.secondary' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* File Structure Dialog */}
      <Dialog 
        open={showFileStructure} 
        onClose={() => setShowFileStructure(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle sx={{ color: 'text.primary' }}>
          File Structure: {editingSite?.domain}
        </DialogTitle>
        <DialogContent>
          {fileStructure ? (
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Path: {fileStructure.sitePath}
              </Typography>
              <List dense sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
                {renderFileTree(fileStructure.fileStructure)}
              </List>
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={() => setShowFileStructure(false)} sx={{ color: 'text.secondary' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Content Editor Dialog */}
      <Dialog 
        open={showContentEditor} 
        onClose={() => setShowContentEditor(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle sx={{ color: 'text.primary' }}>
          Edit Content: {editingSite?.name}
        </DialogTitle>
        <DialogContent>
          {editedContent && (
            <Box sx={{ mt: 2 }}>
              <Accordion defaultExpanded sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}>
                  <Typography variant="h6">Home Page Content</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Page Title"
                        value={editedContent.pages?.home?.title || ''}
                        onChange={(e) => setEditedContent({
                          ...editedContent,
                          pages: {
                            ...editedContent.pages,
                            home: {
                              ...editedContent.pages?.home,
                              title: e.target.value
                            }
                          }
                        })}
                        margin="normal"
                        sx={{
                          '& .MuiInputLabel-root': { color: 'text.secondary' },
                          '& .MuiInputBase-root': { color: 'text.primary' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Hero Title"
                        value={editedContent.pages?.home?.hero?.title || ''}
                        onChange={(e) => setEditedContent({
                          ...editedContent,
                          pages: {
                            ...editedContent.pages,
                            home: {
                              ...editedContent.pages?.home,
                              hero: {
                                ...editedContent.pages?.home?.hero,
                                title: e.target.value
                              }
                            }
                          }
                        })}
                        margin="normal"
                        sx={{
                          '& .MuiInputLabel-root': { color: 'text.secondary' },
                          '& .MuiInputBase-root': { color: 'text.primary' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Hero Subtitle"
                        value={editedContent.pages?.home?.hero?.subtitle || ''}
                        onChange={(e) => setEditedContent({
                          ...editedContent,
                          pages: {
                            ...editedContent.pages,
                            home: {
                              ...editedContent.pages?.home,
                              hero: {
                                ...editedContent.pages?.home?.hero,
                                subtitle: e.target.value
                              }
                            }
                          }
                        })}
                        margin="normal"
                        sx={{
                          '& .MuiInputLabel-root': { color: 'text.secondary' },
                          '& .MuiInputBase-root': { color: 'text.primary' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                        }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}>
                  <Typography variant="h6">About Page Content</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="About Content"
                    value={editedContent.pages?.about?.content || ''}
                    onChange={(e) => setEditedContent({
                      ...editedContent,
                      pages: {
                        ...editedContent.pages,
                        about: {
                          ...editedContent.pages?.about,
                          content: e.target.value
                        }
                      }
                    })}
                    margin="normal"
                    sx={{
                      '& .MuiInputLabel-root': { color: 'text.secondary' },
                      '& .MuiInputBase-root': { color: 'text.primary' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={() => setShowContentEditor(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveContent}
            disabled={!editedContent}
            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </Box>
  );
};

export default SitesPage;
